import { useState, useEffect } from "react";
import { X, Calendar, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import moviLogo from "@/assets/movi-logo.png";

const topNavItems = [
  { label: "About", href: "#about" },
  { label: "News", href: "#news" },
  { label: "Contact", href: "#contact" },
];

const dropdownItems = [
  { label: "About", href: "#about" },
  { label: "News", href: "#news" },
  { label: "Team", href: "#team" },
  { label: "Advisory Board", href: "#advisory" },
  { label: "The MOVI Meeting", href: "#meeting" },
  { label: "MOVI Impact", href: "#numbers" },
  { label: "Supporters", href: "#supporters" },
  { label: "Contact", href: "#contact" },
];

const TicketBackground = () => (
  <svg
    className="absolute inset-0 w-full h-full"
    viewBox="0 0 100 60"
    preserveAspectRatio="none"
    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
  >
    <path
      d="M 8 0 L 42 0 Q 50 6 58 0 L 92 0 Q 100 0 100 8 L 100 22 Q 94 30 100 38 L 100 52 Q 100 60 92 60 L 58 60 Q 50 54 42 60 L 8 60 Q 0 60 0 52 L 0 38 Q 6 30 0 22 L 0 8 Q 0 0 8 0 Z"
      fill="white"
    />
  </svg>
);

const MenuButton = ({ onClick, isScrolled }: { onClick: () => void; isScrolled: boolean }) => (
  <button
    onClick={onClick}
    className={`group flex flex-col gap-[6px] w-7 transition-all duration-300 ease-out hover:gap-[8px] ${
      isScrolled ? "text-foreground" : "text-primary-foreground"
    }`}
    aria-label="Open menu"
  >
    <span className="w-full h-[2px] bg-current transition-all duration-300"></span>
    <span className="w-full h-[2px] bg-current transition-all duration-300"></span>
    <span className="w-full h-[2px] bg-current transition-all duration-300"></span>
  </button>
);

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newsItems, setNewsItems] = useState<{ id: string; title: string; date: string; category: string }[]>([]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  useEffect(() => {
    const fetchNews = async () => {
      const { data } = await supabase
        .from("news_items")
        .select("id, title, date, category")
        .order("display_order", { ascending: false })
        .limit(3);
      if (data) setNewsItems(data);
    };
    fetchNews();
  }, []);

  const scrollToSection = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <nav
        className={`fixed z-50 transition-all duration-500 ease-out ${
          isScrolled
            ? "top-0 left-0 right-0 w-full bg-card/95 backdrop-blur-md shadow-lg"
            : "top-8 md:top-12 left-1/2 -translate-x-1/2 w-[70%] bg-transparent"
        }`}
      >
        <div className="mx-auto px-6">
          <div className={`flex items-center h-16 ${isScrolled ? "justify-between" : "justify-center"}`}>
            {/* Desktop */}
            <div className="hidden lg:flex items-center justify-between w-full">
              <div className="flex items-center gap-6">
                <a href="#" className="flex-shrink-0 relative" onClick={handleLogoClick}>
                  <div className="relative">
                    {!isScrolled && <TicketBackground />}
                    <img 
                      src={moviLogo} 
                      alt="MOVI" 
                      width={117}
                      height={55}
                      className={`w-auto relative z-10 transition-all duration-300 ${
                        isScrolled ? "h-10" : "h-[60px] p-[5px]"
                      }`}
                    />
                  </div>
                </a>

                {topNavItems.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    className={`group tracking-[0.1em] uppercase transition-all duration-200 ${
                      isScrolled ? "text-sm font-medium text-foreground" : "text-base font-medium text-primary-foreground"
                    }`}
                  >
                    <span className="relative inline-block py-1">
                      {item.label}
                      <span className="absolute bottom-0 left-0 w-full h-[3px] bg-movi-sage origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"></span>
                    </span>
                  </button>
                ))}
              </div>

              <MenuButton onClick={() => setIsMenuOpen(true)} isScrolled={isScrolled} />
            </div>

            {/* Mobile */}
            <div className="lg:hidden flex items-center justify-between w-full">
              <a href="#" className="flex-shrink-0 relative" onClick={handleLogoClick}>
                <div className="relative">
                  <TicketBackground />
                  <img src={moviLogo} alt="MOVI" width={117} height={55} className="h-[60px] w-auto relative z-10 p-[5px]" />
                </div>
              </a>
              <MenuButton onClick={() => setIsMenuOpen(true)} isScrolled={isScrolled} />
            </div>
          </div>
        </div>
      </nav>

      {/* Full screen dropdown overlay */}
      <div
        className={`fixed inset-0 z-[100] bg-primary transition-all duration-500 ease-out ${
          isMenuOpen 
            ? "opacity-100 pointer-events-auto translate-y-0" 
            : "opacity-0 pointer-events-none -translate-y-4"
        }`}
      >
        <div className="w-full px-6">
          <div className="flex items-center justify-between h-24">
            <a href="#" className="flex-shrink-0 relative" onClick={handleLogoClick}>
              <div className="relative">
                <TicketBackground />
                <img src={moviLogo} alt="MOVI" width={117} height={55} className="h-[60px] w-auto relative z-10 p-[5px]" />
              </div>
            </a>

            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-primary-foreground transition-transform duration-300 ease-out hover:rotate-90"
              aria-label="Close menu"
            >
              <X size={28} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="h-[calc(100%-96px)] flex items-center justify-center px-6 lg:px-16">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-24">
            {/* Left - Main Nav Items */}
            <div className="flex flex-col gap-1 md:gap-2">
              {dropdownItems.map((item, index) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className={`group text-left text-base font-medium tracking-[0.1em] uppercase text-primary-foreground transition-all duration-200 ${
                    isMenuOpen ? "animate-fade-up" : ""
                  }`}
                  style={{ animationDelay: `${index * 0.04}s`, animationFillMode: "both" }}
                >
                  <span className="relative inline-block py-1">
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-full h-[3px] bg-movi-sage origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"></span>
                  </span>
                </button>
              ))}
            </div>

            {/* Right - Latest News Card */}
            <div className={`border-2 border-movi-sage/60 rounded-2xl p-6 lg:p-8 max-w-md ${isMenuOpen ? "animate-fade-up" : ""}`} style={{ animationDelay: "0.3s", animationFillMode: "both" }}>
              <h3 className="text-sm font-medium tracking-[0.1em] uppercase text-primary-foreground mb-6">
                Latest News
              </h3>
              <div className="flex flex-col gap-5">
                {newsItems.map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => scrollToSection("#news")}
                    className="group text-left"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-movi-sage font-medium">{item.category}</span>
                      <span className="flex items-center gap-1 text-xs text-primary-foreground/50">
                        <Calendar size={10} />
                        {item.date}
                      </span>
                    </div>
                    <p className="text-primary-foreground/90 group-hover:text-primary-foreground transition-colors text-sm font-medium">
                      {item.title}
                    </p>
                  </button>
                ))}
                <button 
                  onClick={() => scrollToSection("#news")}
                  className="flex items-center gap-1 text-movi-sage hover:gap-2 transition-all text-sm font-medium mt-2"
                >
                  View all news
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};